import React, { useState, useCallback } from 'react';
import { Car, Info, ChevronDown, ChevronUp, Upload, X, Image as ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { toast } from 'sonner';

export const VehicleInfo = ({ serviceType, onNext, onBack }) => {
  const { t } = useTranslation();
  const [vehicleData, setVehicleData] = useState({
    year: '',
    make: '',
    model: '',
    tireSize: ''
  });
  const [photos, setPhotos] = useState([]);
  const [showTireSizeHelp, setShowTireSizeHelp] = useState(false);

  const isFlatTire = serviceType === t('roadside.step2.services.flatTire') || serviceType === 'Flat Tire';

  const handleChange = (field, value) => {
    setVehicleData({ ...vehicleData, [field]: value });
  };

  // Photo upload handlers
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    rejectedFiles.forEach((file) => {
      const error = file.errors[0];
      if (error.code === 'file-too-large') {
        toast.error(t('roadside.errors.submitFailedMsg'), {
          description: 'File size exceeds 8MB'
        });
      } else if (error.code === 'file-invalid-type') {
        toast.error('Unsupported file format', {
          description: 'Only JPG, PNG, and HEIC files are allowed'
        });
      }
    });

    // Handle accepted files
    if (photos.length + acceptedFiles.length > 3) {
      toast.error('Maximum 3 photos allowed');
      return;
    }

    const newPhotos = acceptedFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setPhotos(prev => [...prev, ...newPhotos]);
  }, [photos, t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/heic': ['.heic']
    },
    maxSize: 8 * 1024 * 1024, // 8MB
    maxFiles: 3,
    disabled: photos.length >= 3
  });

  const removePhoto = (index) => {
    setPhotos(prev => {
      const newPhotos = [...prev];
      URL.revokeObjectURL(newPhotos[index].preview);
      newPhotos.splice(index, 1);
      return newPhotos;
    });
  };

  const handleNext = () => {
    // Validate required fields
    if (!vehicleData.year || !vehicleData.make || !vehicleData.model) {
      toast.error(t('roadside.errors.vehicleRequired'), {
        description: t('roadside.errors.vehicleRequiredMsg')
      });
      return;
    }

    // Validate year
    const currentYear = new Date().getFullYear();
    const year = parseInt(vehicleData.year);
    if (isNaN(year) || year < 1900 || year > currentYear + 1) {
      toast.error(t('roadside.errors.invalidYear'), {
        description: t('roadside.errors.invalidYearMsg', { year: currentYear + 1 })
      });
      return;
    }

    // Validate tire size if flat tire service
    if (isFlatTire && !vehicleData.tireSize.trim()) {
      toast.error(t('roadside.errors.tireSizeRequired'), {
        description: t('roadside.errors.tireSizeRequiredMsg')
      });
      return;
    }

    onNext({
      vehicle_year: vehicleData.year,
      vehicle_make: vehicleData.make,
      vehicle_model: vehicleData.model,
      tire_size: isFlatTire ? vehicleData.tireSize : null,
      photos: photos.map(p => p.file) // Pass files for upload
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-50 pb-safe">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900">{t('roadside.step3.title')}</h1>
          <p className="text-sm text-gray-600 mt-1">{t('roadside.step3.step')}: {t('roadside.step3.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Service Type Badge */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-3">
            <Car className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-sm text-blue-800 font-medium">{t('roadside.step3.serviceType')} {serviceType}</p>
            </div>
          </div>
        </Card>

        {/* Vehicle Details Form */}
        <Card className="p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-6">{t('roadside.step3.vehicleDetails')}</h2>

          <div className="space-y-4">
            {/* Year */}
            <div>
              <Label htmlFor="year" className="text-base font-semibold mb-2 block">
                {t('roadside.step3.year')} *
              </Label>
              <Input
                id="year"
                type="number"
                value={vehicleData.year}
                onChange={(e) => handleChange('year', e.target.value)}
                placeholder="2020"
                min="1900"
                max={new Date().getFullYear() + 1}
                className="text-base numeric"
                required
              />
            </div>

            {/* Make */}
            <div>
              <Label htmlFor="make" className="text-base font-semibold mb-2 block">
                {t('roadside.step3.make')} *
              </Label>
              <Input
                id="make"
                value={vehicleData.make}
                onChange={(e) => handleChange('make', e.target.value)}
                placeholder={t('roadside.step3.makePlaceholder')}
                className="text-base"
                required
              />
            </div>

            {/* Model */}
            <div>
              <Label htmlFor="model" className="text-base font-semibold mb-2 block">
                {t('roadside.step3.model')} *
              </Label>
              <Input
                id="model"
                value={vehicleData.model}
                onChange={(e) => handleChange('model', e.target.value)}
                placeholder={t('roadside.step3.modelPlaceholder')}
                className="text-base"
                required
              />
            </div>
          </div>
        </Card>

        {/* Conditional Tire Size Field */}
        {isFlatTire && (
          <Card className="p-6 border-2 border-orange-200 bg-orange-50 animate-fade-in">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-1">{t('roadside.step3.tireSizeRequired')}</h3>
                <p className="text-sm text-gray-700">
                  {t('roadside.step3.tireSizeHelp')}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="tireSize" className="text-base font-semibold mb-2 block">
                  {t('roadside.step3.tireSize')} *
                </Label>
                <Input
                  id="tireSize"
                  value={vehicleData.tireSize}
                  onChange={(e) => handleChange('tireSize', e.target.value.toUpperCase())}
                  placeholder={t('roadside.step3.tireSizePlaceholder')}
                  className="text-base font-mono bg-white tire-size"
                  required
                />
              </div>

              {/* Help Section */}
              <div className="border-t border-orange-200 pt-4">
                <button
                  onClick={() => setShowTireSizeHelp(!showTireSizeHelp)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <span className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <Info className="w-4 h-4 text-orange-600" />
                    {t('roadside.step3.whereToFind')}
                  </span>
                  {showTireSizeHelp ? (
                    <ChevronUp className="w-4 h-4 text-gray-600" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-600" />
                  )}
                </button>

                {showTireSizeHelp && (
                  <div className="mt-3 p-4 bg-white rounded-lg border border-orange-200 text-sm text-gray-700 space-y-2">
                    <p className="font-semibold text-gray-900">{t('roadside.step3.tireSizeInfo')}</p>
                    <ul className="list-disc ltr:pl-5 rtl:pr-5 space-y-1">
                      <li>{t('roadside.step3.doorJamb')}</li>
                      <li>{t('roadside.step3.sidewall')}</li>
                    </ul>
                    <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
                      <p className="font-semibold text-gray-900 mb-1">{t('roadside.step3.exampleFormat')}</p>
                      <p className="font-mono text-lg text-orange-600 tire-size">215/55R16</p>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('roadside.step3.sidewallLocation')}
                      </p>
                    </div>
                    <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                      <p className="text-xs text-blue-800">
                        <strong>💡</strong> {t('roadside.step3.tireSizeTip')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Photo Upload Section */}
        <Card className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <ImageIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 mb-1">{t('roadside.step3.photosTitle')}</h3>
              <p className="text-sm text-gray-700">
                {t('roadside.step3.photosHelp')}
              </p>
            </div>
          </div>

          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-purple-500 bg-purple-50'
                : photos.length >= 3
                ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'
            }`}
          >
            <input {...getInputProps()} disabled={photos.length >= 3} />
            <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            {photos.length >= 3 ? (
              <p className="text-sm text-gray-500">
                Maximum 3 photos reached
              </p>
            ) : isDragActive ? (
              <p className="text-sm text-purple-600 font-semibold">
                Drop photos here...
              </p>
            ) : (
              <div>
                <p className="text-sm text-gray-700 font-semibold mb-1">
                  {t('roadside.step3.dropzone')}
                </p>
                <p className="text-xs text-gray-500">
                  {t('roadside.step3.photoTypes')}
                </p>
              </div>
            )}
          </div>

          {/* Photo Previews */}
          {photos.length > 0 && (
            <div className="mt-4 grid grid-cols-3 gap-3">
              {photos.map((photo, index) => (
                <div key={index} className="relative group">
                  <img
                    src={photo.preview}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                  />
                  <button
                    onClick={() => removePhoto(index)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Summary Preview */}
        <Card className="p-4 bg-gray-50">
          <p className="text-sm text-gray-600 mb-2">{t('roadside.step3.vehicleSummary')}</p>
          <p className="font-semibold text-gray-900">
            {vehicleData.year || '____'} {vehicleData.make || '____'} {vehicleData.model || '____'}
            {isFlatTire && vehicleData.tireSize && (
              <span className="ltr:ml-2 rtl:mr-2 text-orange-600 tire-size">| {t('roadside.step3.tireSize')}: {vehicleData.tireSize}</span>
            )}
          </p>
          {photos.length > 0 && (
            <p className="text-sm text-purple-600 mt-2">
              📸 {photos.length} photo(s) attached
            </p>
          )}
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            onClick={onBack}
            variant="outline"
            size="lg"
            className="flex-1 border-2 py-6"
          >
            {t('common.back')}
          </Button>
          <Button
            onClick={handleNext}
            size="lg"
            className="flex-1 bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-semibold shadow-lg"
          >
            {t('common.continue')}
          </Button>
        </div>

        {/* Required Fields Note */}
        <p className="text-xs text-gray-500 text-center">
          {t('roadside.step3.requiredNote')}
        </p>
      </div>
    </div>
  );
};
